<template>
	<Teleport to="body">
		<div
			v-if="openState"
			class="preset-manager-overlay"
			@pointerdown.stop
			@click.self="cancel"
		>
			<section
				ref="dialogRef"
				class="preset-manager"
				role="dialog"
				aria-modal="true"
				:aria-labelledby="titleId"
				tabindex="-1"
				@keydown.esc.prevent.stop="cancel"
				@keydown.tab="trapFocus"
			>
				<header class="preset-manager__header">
					<div>
						<h3 :id="titleId">{{ title }}</h3>
						<p v-if="description">{{ description }}</p>
					</div>
					<button
						type="button"
						class="preset-manager__icon-button"
						:disabled="saving"
						:aria-label="closeLabel"
						@click="cancel"
					>
						×
					</button>
				</header>

				<div class="preset-manager__body">
					<div v-if="rows.length" class="preset-manager__list">
						<div v-for="(row, index) in rows" :key="row.id" class="preset-manager__row">
							<input
								v-model="row.value"
								type="text"
								:maxlength="maxLength"
								:aria-label="`${optionLabel} ${index + 1}`"
								@keydown.enter.prevent="addRow(index + 1)"
							/>
							<button
								type="button"
								class="preset-manager__remove"
								:aria-label="`${removeOptionLabel} ${index + 1}`"
								@click="removeRow(index)"
							>
								×
							</button>
						</div>
					</div>
					<div v-else class="preset-manager__empty">
						No values yet. Add the first one below.
					</div>

					<button
						ref="addButtonRef"
						type="button"
						class="preset-manager__add"
						:disabled="rows.length >= maxOptions"
						@click="addRow()"
					>
						<span aria-hidden="true">＋</span>
						Add value
					</button>
					<p v-if="errorMessage" class="preset-manager__error" role="alert">{{ errorMessage }}</p>
				</div>

				<footer class="preset-manager__footer">
					<span class="preset-manager__count">{{ normalizedValues.length }}/{{ maxOptions }} values</span>
					<div class="preset-manager__actions">
						<button type="button" class="preset-manager__button" :disabled="saving" @click="cancel">
							Cancel
						</button>
						<button
							type="button"
							class="preset-manager__button preset-manager__button--primary"
							:disabled="saving"
							@click="save"
						>
							{{ saving ? 'Saving…' : 'Save values' }}
						</button>
					</div>
				</footer>
			</section>
		</div>
	</Teleport>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onUnmounted, ref } from 'vue';
import {
	MAX_ASSIGNMENT_SUBTITLE_LENGTH,
	MAX_ASSIGNMENT_SUBTITLE_OPTIONS,
} from '@/utils/assignmentSubtitlePresets';

const props = defineProps({
	options: {
		type: Array,
		default: () => [],
	},
	title: {
		type: String,
		default: 'Manage saved values',
	},
	description: {
		type: String,
		default: 'These values are available throughout this project.',
	},
	closeLabel: {
		type: String,
		default: 'Close saved values',
	},
	optionLabel: {
		type: String,
		default: 'Saved value',
	},
	removeOptionLabel: {
		type: String,
		default: 'Remove saved value',
	},
	saveErrorMessage: {
		type: String,
		default: 'Could not save values. Please try again.',
	},
	saveOptions: {
		type: Function,
		default: null,
	},
	maxOptions: {
		type: Number,
		default: MAX_ASSIGNMENT_SUBTITLE_OPTIONS,
	},
	maxLength: {
		type: Number,
		default: MAX_ASSIGNMENT_SUBTITLE_LENGTH,
	},
});

const emit = defineEmits(['open', 'close', 'saved']);

const instanceId = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2);
const titleId = `preset-manager-${instanceId}-title`;
const dialogRef = ref(null);
const addButtonRef = ref(null);
const openState = ref(false);
const rows = ref([]);
const saving = ref(false);
const errorMessage = ref('');
let rowSequence = 0;
let returnFocusElement = null;

const normalizeValues = (values) => {
	const normalized = [];
	const seen = new Set();
	for (const value of Array.isArray(values) ? values : []) {
		if (normalized.length >= props.maxOptions) break;
		const option = String(value ?? '')
			.trim()
			.slice(0, props.maxLength);
		const key = option.toLocaleLowerCase();
		if (!option || seen.has(key)) continue;
		seen.add(key);
		normalized.push(option);
	}
	return normalized;
};

const normalizedValues = computed(() => normalizeValues(rows.value.map((row) => row.value)));
const createRow = (value = '') => ({
	id: `preset-option-${Date.now()}-${rowSequence++}`,
	value,
});

const modalEnvironment = {
	bodyOverflow: null,
	appRoot: null,
	appRootWasInert: false,
};

const lockEnvironment = () => {
	if (typeof document === 'undefined' || modalEnvironment.bodyOverflow !== null) return;
	modalEnvironment.bodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
	const appRoot = document.getElementById('app');
	if (appRoot && !appRoot.contains(dialogRef.value)) {
		modalEnvironment.appRoot = appRoot;
		modalEnvironment.appRootWasInert = Boolean(appRoot.inert);
		appRoot.inert = true;
	}
};

const unlockEnvironment = () => {
	if (typeof document === 'undefined' || modalEnvironment.bodyOverflow === null) return;
	document.body.style.overflow = modalEnvironment.bodyOverflow;
	modalEnvironment.bodyOverflow = null;
	if (modalEnvironment.appRoot) {
		modalEnvironment.appRoot.inert = modalEnvironment.appRootWasInert;
		modalEnvironment.appRoot = null;
		modalEnvironment.appRootWasInert = false;
	}
};

const open = ({ returnFocus = null } = {}) => {
	if (openState.value) return;
	returnFocusElement = returnFocus;
	rows.value = normalizeValues(props.options).map((option) => createRow(option));
	errorMessage.value = '';
	openState.value = true;
	emit('open');
	nextTick(() => {
		lockEnvironment();
		const firstInput = dialogRef.value?.querySelector('input');
		(firstInput || addButtonRef.value || dialogRef.value)?.focus?.();
	});
};

const close = ({ restoreFocus = true } = {}) => {
	if (saving.value || !openState.value) return;
	openState.value = false;
	errorMessage.value = '';
	unlockEnvironment();
	emit('close');
	const focusTarget = returnFocusElement;
	returnFocusElement = null;
	if (restoreFocus) nextTick(() => focusTarget?.focus?.({ preventScroll: true }));
};

const cancel = () => close();

const addRow = (insertAt = rows.value.length) => {
	if (rows.value.length >= props.maxOptions) return;
	const index = Math.min(Math.max(Number(insertAt) || 0, 0), rows.value.length);
	rows.value.splice(index, 0, createRow());
	nextTick(() => dialogRef.value?.querySelectorAll('input')?.[index]?.focus?.());
};

const removeRow = (index) => {
	rows.value.splice(index, 1);
	nextTick(() => {
		const inputs = dialogRef.value?.querySelectorAll('input');
		(inputs?.[Math.min(index, inputs.length - 1)] || addButtonRef.value)?.focus?.();
	});
};

const save = async () => {
	if (saving.value) return;
	saving.value = true;
	errorMessage.value = '';
	const values = normalizedValues.value;
	try {
		if (typeof props.saveOptions === 'function') await props.saveOptions(values);
		emit('saved', values);
		saving.value = false;
		close();
	} catch (error) {
		saving.value = false;
		errorMessage.value = error?.message || props.saveErrorMessage;
	}
};

const trapFocus = (event) => {
	const focusable = Array.from(
		dialogRef.value?.querySelectorAll(
			'button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
		) || [],
	);
	if (!focusable.length) {
		event.preventDefault();
		dialogRef.value?.focus?.();
		return;
	}
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (document.activeElement === dialogRef.value) {
		event.preventDefault();
		(event.shiftKey ? last : first).focus?.();
	} else if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last.focus?.();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus?.();
	}
};

defineExpose({ open, close });
onUnmounted(unlockEnvironment);
</script>

<style scoped>
.preset-manager-overlay {
	position: fixed;
	z-index: 1400;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(15, 23, 42, 0.46);
	box-sizing: border-box;
}

.preset-manager {
	display: flex;
	flex-direction: column;
	width: min(580px, calc(100vw - 32px));
	max-height: min(680px, calc(100vh - 32px));
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 12px;
	outline: none;
	background: var(--noc-background, #fff);
	box-shadow: 0 22px 60px rgba(15, 23, 42, 0.24);
	overflow: hidden;
}

.preset-manager__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px;
	padding: 22px 22px 16px;
}

.preset-manager__header h3 {
	margin: 0;
	color: var(--text-color, #333);
	font-size: 1.15rem;
	font-weight: 650;
}

.preset-manager__header p {
	margin: 5px 0 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.84rem;
	line-height: 1.4;
}

.preset-manager__icon-button,
.preset-manager__remove {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: 0;
	background: transparent;
	color: var(--text-color-secondary, #6b7280);
	font: inherit;
	cursor: pointer;
}

.preset-manager__icon-button {
	flex: 0 0 32px;
	width: 32px;
	height: 32px;
	border-radius: 6px;
	font-size: 1.45rem;
	line-height: 1;
}

.preset-manager__icon-button:hover,
.preset-manager__icon-button:focus-visible,
.preset-manager__remove:hover,
.preset-manager__remove:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	color: var(--text-color, #333);
	outline: none;
}

.preset-manager__body {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	gap: 12px;
	min-height: 0;
	padding: 0 22px 18px;
	overflow-y: auto;
}

.preset-manager__list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.preset-manager__row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 34px;
	align-items: center;
	gap: 8px;
}

.preset-manager__row input {
	width: 100%;
	height: 38px;
	padding: 6px 11px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 7px;
	outline: none;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.85rem;
	box-sizing: border-box;
}

.preset-manager__row input:hover {
	border-color: var(--border-color-active, #9ca3af);
}

.preset-manager__row input:focus {
	border-color: var(--noc-blue, #1261c9);
}

.preset-manager__remove {
	width: 34px;
	height: 34px;
	border-radius: 6px;
	font-size: 1.25rem;
}

.preset-manager__empty {
	padding: 18px;
	border: 1px dashed var(--border-color, #d1d5db);
	border-radius: 8px;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.85rem;
	text-align: center;
}

.preset-manager__add {
	display: inline-flex;
	align-self: flex-start;
	align-items: center;
	gap: 6px;
	padding: 7px 10px;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--primary-color, #1261c9);
	font: inherit;
	font-size: 0.84rem;
	font-weight: 600;
	cursor: pointer;
}

.preset-manager__add:hover,
.preset-manager__add:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	outline: none;
}

.preset-manager__add:disabled,
.preset-manager__icon-button:disabled {
	opacity: 0.55;
	cursor: default;
}

.preset-manager__error {
	margin: 0;
	color: #b42318;
	font-size: 0.82rem;
}

.preset-manager__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 14px 22px;
	border-top: 1px solid var(--border-color, #e5e7eb);
	background: var(--noc-background-secondary, #fafafb);
}

.preset-manager__count {
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.78rem;
}

.preset-manager__actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.preset-manager__button {
	min-height: 36px;
	padding: 7px 14px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 7px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.84rem;
	font-weight: 600;
	cursor: pointer;
}

.preset-manager__button:hover,
.preset-manager__button:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	outline: none;
}

.preset-manager__button--primary {
	border-color: var(--primary-color, #1261c9);
	background: var(--primary-color, #1261c9);
	color: #fff;
}

.preset-manager__button--primary:hover,
.preset-manager__button--primary:focus-visible {
	background: var(--primary-color-hover, #0d4fa7);
}

.preset-manager__button:disabled {
	opacity: 0.6;
	cursor: wait;
}

@media (max-width: 600px) {
	.preset-manager__footer {
		align-items: stretch;
		flex-direction: column;
	}

	.preset-manager__actions,
	.preset-manager__button {
		width: 100%;
	}

	.preset-manager__button {
		flex: 1 1 0;
	}
}
</style>
